// ClikMeet's room page, /r/<room> (M3; D127 A): the one link a ClikWork calendar invite carries. A clikin.ai colleague
// signs in with ClikWork, which sends them straight to their own link (the organiser's is the host's). Anyone else checks
// their camera, types a name and asks to join, waits here until the host (or a colleague) admits them, and is then
// taken straight into the call (CR-20260926-001 U4).
import { getRoom, knockAt, knockState } from "./api.js";
import { firstName, h } from "./dom.js";
import { formatWhen, huddleClosed } from "./format.js";
import { product, wordmark } from "./product.js";
import { lobby } from "./lobby.js";

const WAIT_POLL_MS = 1500; // Admit to the call in under 3 s (CR-20260926-001 U4)

function page(root, ...body) {
  root.replaceChildren(h("div", { class: "page" },
    h("header", { class: "page-head" }, wordmark()),
    h("main", { class: "page-main" }, h("div", { class: "end" }, ...body))));
}

export async function roomPage(root, room, { enter } = {}) {
  document.title = product.title;
  let info;
  try {
    info = await getRoom(room);
  } catch (err) {
    page(root, h("h1", { class: "statement", text: err.status === 404 ? "This link does not open a meeting." : "We could not open this meeting." }),
      h("p", { class: "lede", text: err.status === 404 ? "Ask whoever invited you for the link again." : "Check your connection and open the link again." }));
    return;
  }
  const { meeting, window: win, signIn } = info;
  const when = `${formatWhen(meeting.startsAt)} · ${meeting.durationMin} minutes · with ${meeting.hostName}`;
  const closed = huddleClosed(meeting, win);
  if (closed) {
    page(root, h("p", { class: "eyebrow", text: product.name }), h("h1", { class: "statement", text: closed.button }),
      h("p", { class: "lede", text: closed.line }));
    return;
  }
  if (win.state !== "open") {
    const say = { early: `This meeting opens at ${formatWhen(win.opensAt)}, an hour before it starts. This same link will work then.`,
      over: "This meeting is over.", ended: "This meeting has ended.", cancelled: "This meeting was cancelled." }[win.state] || "This meeting is not open.";
    page(root, h("p", { class: "eyebrow", text: product.name }), h("h1", { class: "statement", text: meeting.title }),
      h("p", { class: "lede", text: when }), h("p", { class: "notice", role: "status", text: say }));
    return;
  }
  // The guest checks their camera and microphone first, on the same lobby everyone uses, then asks to join; on Admit
  // they go straight into the call with what they chose — Zoom's and Teams' order: preview, waiting room, call
  // (CR-20260926-001 U4). A colleague still signs in with ClikWork, from the same page.
  const extra = [
    signIn ? h("div", { class: "actions" }, h("a", { class: "btn btn-ghost", href: signIn, text: "I'm at Clikin: sign in with Workin" })) : null,
    h("p", { class: "fine", text: signIn ? "Not at Clikin? Ask to join, and the host will let you in." : "Ask to join, and the host will let you in." }),
  ];
  const guest = { meeting, window: win, you: { role: "guest", name: "", needsName: true } };
  for (;;) {
    const joined = await lobby(root, guest, { joinLabel: "Ask to join", extra, nameMissing: "Type your name, so the host knows who is asking." });
    let k;
    try {
      k = await knockAt(room, joined.name);
    } catch (err) {
      stopTracks(joined);
      guest.notice = err.message || "We could not ask the host just now. Try again.";
      continue;
    }
    waiting(root, room, meeting, joined, k, enter);
    return;
  }
}

const stopTracks = (joined) => { for (const t of [joined.audioTrack, joined.videoTrack]) { try { t?.stop(); } catch { /* already stopped */ } } };

// The guest waits here, still seeing themself, with the camera and microphone they chose. The page asks every few
// seconds; the answer is the host's (or a colleague's), and an admitted guest goes straight into the call.
function waiting(root, room, meeting, joined, first, enter) {
  let k = first;
  const who = joined.name;
  const self = h("video", { autoplay: true, playsinline: true, muted: true, class: "waiting-self" });
  if (joined.videoTrack && joined.camOn) joined.videoTrack.attach(self);
  const line = h("p", { class: "lede", text: `We have told ${firstName(meeting.hostName)} you are here. You will join as soon as someone lets you in.` });
  page(root, h("p", { class: "eyebrow", text: meeting.title }), h("h1", { class: "statement", text: `Waiting for ${firstName(meeting.hostName)}` }),
    line, joined.videoTrack && joined.camOn ? h("div", { class: "preview waiting-preview" }, self) : null,
    h("p", { class: "fine", text: `Asked as ${who}. Keep this page open.` }));
  let stopped = false;
  const finish = (...body) => { stopped = true; if (joined.videoTrack) joined.videoTrack.detach(self); stopTracks(joined); page(root, ...body); };
  const tick = async () => {
    if (stopped) return;
    try {
      const st = await knockState(room, k.id, k.secret);
      if (st.status === "admitted" && st.code) {
        stopped = true;
        if (joined.videoTrack) joined.videoTrack.detach(self);
        history.replaceState(null, "", `/m/${st.code}`); // a reload opens their own link
        // Which room let them in, for this tab: when the host ends the call their own link is withdrawn (Ravi, 26 Sep),
        // and the end screen sends them back to this door instead (end.js).
        try { sessionStorage.setItem(`clikmeet-door:${st.code}`, room); } catch { /* private mode: the end screen says so */ }
        enter(st.code, joined);
        return;
      }
      if (st.status === "refused") return finish(h("h1", { class: "statement", text: "The host did not let you in." }), h("p", { class: "lede", text: "If you think that was a mistake, ask whoever invited you." }));
      if (st.status === "closed") return finish(h("h1", { class: "statement", text: "This meeting has ended." }), h("p", { class: "lede", text: "The host ended it before letting you in." }));
    } catch (err) {
      // The knock is gone: a phone froze this tab long enough for the door to let it go (lib/store.js deletes knocks
      // nobody is waiting on). Knock again with the same name and keep waiting — never wait for ever on a knock the
      // host can no longer see (re-audit of f15fe731, NEW-1). A dropped request is simply asked again.
      if (err.status === 404) {
        try { k = await knockAt(room, who); } catch (again) {
          // Only the studio's own "no" ends the wait (a 4xx: the room is gone, closed, or the door is full). A dropped
          // connection or a hiccup keeps the old knock, whose next ask 404s and knocks again.
          if (!(again.status >= 400 && again.status < 500)) { setTimeout(tick, WAIT_POLL_MS); return; }
          const why = again.status === 404 ? "This meeting link no longer opens a meeting." : (again.message || "We could not ask the host again.");
          return finish(h("h1", { class: "statement", text: "Your request to join lapsed." }), h("p", { class: "lede", text: why }),
            h("div", { class: "actions" }, h("a", { class: "btn", href: `/r/${room}`, text: "Ask again" })));
        }
      }
    }
    setTimeout(tick, WAIT_POLL_MS);
  };
  setTimeout(tick, WAIT_POLL_MS);
}
